import {
  NewPermission,
  NewPermissionV3,
  Permission,
  PermissionsV3ApiQueryRequest,
  PermissionV3,
} from "../models/permissions";
import { PostBasedPaginatedResponse } from "../models/shared";
import {
  AssetsPaginatedResponse,
  AssetsResponse,
  CursorState,
  ProjectId,
} from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";

export const getPermissionsClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => {
  const baseV2 = http.getAssetsServiceBasePath(projectId, "v2");
  const baseV3 = http.getAssetsServiceBasePath(projectId, "v3");

  return {
    get: (id: Permission["id"]): Promise<Permission> =>
      http.get<Permission>(`${baseV2}/${Endpoints.Permissions}/${id}`),

    list: (
      cursorState?: CursorState
    ): Promise<AssetsPaginatedResponse<Permission[]>> =>
      http.get<AssetsPaginatedResponse<Permission[]>>(
        `${baseV2}/${Endpoints.Permissions}${
          cursorState ? `?cursorState=${cursorState}` : ""
        }`
      ),

    create: (permission: NewPermission): Promise<Permission> =>
      http.post<Permission>(`${baseV2}/${Endpoints.Permissions}`, {
        body: JSON.stringify(permission),
      }),

    update: ({
      id,
      ...permission
    }: Pick<Permission, "id" | "permissionLevels">): Promise<Permission> =>
      http.patch<Permission>(`${baseV2}/${Endpoints.Permissions}/${id}`, {
        body: JSON.stringify(permission),
      }),

    delete: (permissionId: Permission["id"]): Promise<void> =>
      http.del<void>(`${baseV2}/${Endpoints.Permissions}/${permissionId}`),

    listV3: (
      request: PermissionsV3ApiQueryRequest
    ): Promise<
      PostBasedPaginatedResponse<PermissionV3, PermissionsV3ApiQueryRequest>
    > =>
      http.post<
        PostBasedPaginatedResponse<PermissionV3, PermissionsV3ApiQueryRequest>
      >(`${baseV3}/${Endpoints.Permissions}:query`, {
        body: JSON.stringify(request),
      }),

    batchCreateV3: (
      permissions: NewPermissionV3[]
    ): Promise<AssetsResponse<PermissionV3[]>> =>
      http.post<AssetsResponse<PermissionV3[]>>(
        `${baseV3}/${Endpoints.Permissions}:batch-create`,
        { body: JSON.stringify(permissions) }
      ),

    batchDeleteV3: (ids: PermissionV3["id"][]): Promise<void> =>
      http.post<void>(`${baseV3}/${Endpoints.Permissions}:batch-delete`, {
        body: JSON.stringify({ ids }),
      }),
  };
};

/**
 * The shape returned by {@link getPermissionsClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getPermissionsClient>`.
 */
export type PermissionsClient = ReturnType<typeof getPermissionsClient>;
